import React, { useState, useRef } from "react";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  TextField,
  Typography,
  InputAdornment,
} from "@mui/material";
import Fade from "@mui/material/Fade";
import { useTheme } from "@mui/material/styles";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import LockIcon from "@mui/icons-material/Lock";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import api from "../../utilities/api";
import { setCookie, clearCookie } from "../../utilities/cookies";

const Login = () => {
  const theme = useTheme();
  const [step, setStep] = useState(1); // 1: credentials, 2: code
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState(Array(6).fill(""));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const inputsRef = useRef(Array.from({ length: 6 }, () => React.createRef()));

  const canContinue = email.trim() && password.trim();

  const goToCodeStep = async () => {
    if (!canContinue) return;
    setError("");
    setLoading(true);
    try {
      const response = await api.post("/api/auth/login", { email, password });
      if (response.data?.success) {
        setStep(2);
        setTimeout(() => inputsRef.current[0]?.current?.focus(), 250);
      } else {
        setError(response.data?.error || "Unable to send verification code");
      }
    } catch (err) {
      setError(err.response?.data?.error || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    const digit = value.replace(/\D/g, "").slice(-1);
    const next = [...otp];
    next[index] = digit;
    setOtp(next);
    if (digit && index < inputsRef.current.length - 1) {
      inputsRef.current[index + 1]?.current?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputsRef.current[index - 1]?.current?.focus();
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        p: 2,
        bgcolor: "background.default",
      }}
    >
      <Card
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: 480,
          boxShadow: "none",
          bgcolor: "background.default",
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Stack alignItems="center" spacing={2} sx={{ mb: 3 }}>
            <Typography variant="h5" fontWeight={600}>
              Welcome back
            </Typography>
            <Typography variant="body2" color="text.secondary" align="center">
              Sign in to continue to Admin
            </Typography>
          </Stack>

          <Fade in={step === 1} unmountOnExit>
            <Box>
              <Stack spacing={2}>
                <TextField
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <MailOutlineIcon />
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  label="Password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon />
                      </InputAdornment>
                    ),
                  }}
                />
                <Button
                  variant="contained"
                  size="large"
                  endIcon={<ArrowForwardIcon />}
                  disabled={!canContinue || loading}
                  onClick={goToCodeStep}
                >
                  {loading ? "Sending code..." : "Continue"}
                </Button>
              </Stack>
              {error && (
                <Typography variant="caption" color="error" sx={{ mt: 1, display: "block" }}>
                  {error}
                </Typography>
              )}
            </Box>
          </Fade>

          <Fade in={step === 2} unmountOnExit>
            <Box>
              <Stack spacing={2} sx={{ mb: 1 }}>
                <Typography variant="h6" fontWeight={600}>
                  Enter 6-digit code
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  We sent a verification code. Enter it below.
                </Typography>
              </Stack>

              <Stack direction="row" spacing={1.5} justifyContent="center" sx={{ mb: 3 }}>
                {otp.map((digit, i) => (
                  <TextField
                    key={i}
                    value={digit}
                    inputRef={inputsRef.current[i]}
                    onChange={(e) => handleOtpChange(i, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(i, e)}
                    inputProps={{
                      inputMode: "numeric",
                      pattern: "[0-9]*",
                      maxLength: 1,
                      style: { textAlign: "center", fontSize: "1.6rem", padding: "10px 0", width: "56px" },
                    }}
                  />
                ))}
              </Stack>

              <Stack direction="row" spacing={2}>
                <Button
                  variant="contained"
                  fullWidth
                  size="large"
                  onClick={async () => {
                    const code = otp.join("");
                    if (code.length !== 6) return setError("Please enter the 6-digit code");
                    setLoading(true);
                    setError("");
                    try {
                      const response = await api.post("/api/auth/verify", { email, code });
                      const { success, token, user, error: errMsg } = response.data || {};
                      if (!success || !token || !user) {
                        return setError(errMsg || "Verification failed");
                      }
                      // Store token and user
                      clearCookie("accessToken");
                      setCookie("accessToken", token);
                      // Maintain role cookie for components still relying on it
                      setCookie("role", "admin");
                      localStorage.setItem("user", JSON.stringify(user));
                      // Navigate to dashboard
                      window.location.href = "/";
                    } catch (err) {
                      setError(err.response?.data?.error || "Verification failed");
                    } finally {
                      setLoading(false);
                    }
                  }}
                >
                  {loading ? "Verifying..." : "Verify"}
                </Button>
                <Button variant="outlined" fullWidth size="large" onClick={() => setStep(1)}>
                  Back
                </Button>
              </Stack>
              {error && (
                <Typography variant="caption" color="error" sx={{ mt: 1, display: "block" }}>
                  {error}
                </Typography>
              )}
            </Box>
          </Fade>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Login;